import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  Dashboard as DashboardIcon,
  TrendingUp as TrendingUpIcon,
  Insights as InsightsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const MasterDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [aggregatedStats, setAggregatedStats] = useState({
    totalOrganizations: 0,
    activeOrganizations: 0,
    totalFirstTimers: 0,
    totalNewConverts: 0,
    totalPendingFollowUps: 0,
    totalCompletedFollowUps: 0,
    totalUsers: 0
  });
  const [orgStats, setOrgStats] = useState([]);

  useEffect(() => {
    const isPlatformSuperAdmin =
      user?.role === 'system_admin' && !user?.organization_id;

    if (!user || !isPlatformSuperAdmin) {
      if (user?.organization_slug) {
        navigate(`/${user.organization_slug}/dashboard`, { replace: true });
      } else {
        navigate('/not-found', { replace: true });
      }
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      // Fetch aggregated stats from backend
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3003'}/api/organizations/stats/aggregated`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const { aggregated, organizations } = response.data;

      setAggregatedStats(aggregated);
      setOrganizations(organizations.map(org => ({
        id: org.id,
        name: org.name,
        slug: org.slug,
        subdomain: org.subdomain,
        is_active: org.is_active
      })));

      setOrgStats(organizations.map(org => ({
        organization: {
          id: org.id,
          name: org.name,
          slug: org.slug,
          subdomain: org.subdomain,
          is_active: org.is_active
        },
        firstTimers: org.firstTimers,
        newConverts: org.newConverts,
        pendingFollowUps: org.pendingFollowUps,
        completedFollowUps: org.completedFollowUps,
        users: org.users
      })));
    } catch (err) {
      console.error('Error fetching master dashboard data:', err);
      setError(err.response?.data?.error || 'Failed to load dashboard data');
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleManageOrganizations = () => {
    navigate('/organization-management');
  };

  const handleCommunicationAnalytics = () => {
    navigate('/communication-analytics');
  };

  const handleViewOrganization = (orgSlug) => {
    navigate(`/${orgSlug}/dashboard`);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 8 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress size={60} sx={{ color: '#DC2626' }} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 8 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' } }}>
        <Box>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700, color: '#DC2626', mb: 1 }}>
            Master Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            National overview of all church branches
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          <Button variant="outlined" startIcon={<InsightsIcon />} onClick={handleCommunicationAnalytics}>
            Communication Analytics
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleManageOrganizations}
            sx={{ backgroundColor: '#DC2626', '&:hover': { backgroundColor: '#B91C1C' } }}
          >
            Manage Organizations
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {/* Aggregated Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', boxShadow: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon sx={{ fontSize: 40, color: '#DC2626', mr: 2 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {aggregatedStats.totalOrganizations}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Churches
                  </Typography>
                </Box>
              </Box>
              <Chip
                label={`${aggregatedStats.activeOrganizations} Active`}
                size="small"
                sx={{ backgroundColor: '#10b981', color: 'white', fontWeight: 600 }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', boxShadow: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PeopleIcon sx={{ fontSize: 40, color: '#f59e0b', mr: 2 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {aggregatedStats.totalFirstTimers.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total First Timers
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ mt: 1 }}>
                <Chip
                  label={`${aggregatedStats.totalPendingFollowUps} Pending`}
                  size="small"
                  sx={{ backgroundColor: '#fef3c7', color: '#92400e', mr: 1 }}
                />
                {aggregatedStats.totalFirstTimers > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {Math.round((aggregatedStats.totalCompletedFollowUps / aggregatedStats.totalFirstTimers) * 100)}% Completion Rate
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', boxShadow: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CheckCircleIcon sx={{ fontSize: 40, color: '#10b981', mr: 2 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {aggregatedStats.totalCompletedFollowUps.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed Follow-ups
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#10b981' }}>
                  {aggregatedStats.totalNewConverts.toLocaleString()} New Converts
                </Typography>
                {aggregatedStats.totalCompletedFollowUps > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {Math.round((aggregatedStats.totalNewConverts / aggregatedStats.totalCompletedFollowUps) * 100)}% Conversion Rate
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', boxShadow: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AssignmentIcon sx={{ fontSize: 40, color: '#06b6d4', mr: 2 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {aggregatedStats.totalUsers.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Users
                  </Typography>
                </Box>
              </Box>
              {aggregatedStats.totalOrganizations > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Avg. {Math.round(aggregatedStats.totalUsers / aggregatedStats.totalOrganizations)} users per church
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Organizations Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Church Branches Overview
            </Typography>
            <Button
              variant="outlined"
              startIcon={<TrendingUpIcon />}
              onClick={fetchData}
              size="small"
            >
              Refresh
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Church Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">First Timers</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">New Converts</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Pending</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Completed</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Users</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orgStats.map((stat) => (
                  <TableRow key={stat.organization.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <BusinessIcon sx={{ mr: 1, color: '#DC2626' }} />
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {stat.organization.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {stat.organization.slug}
                      </Typography>
                      {stat.organization.subdomain && (
                        <Typography variant="caption" color="text.secondary">
                          {stat.organization.subdomain}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {stat.firstTimers.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {stat.newConverts.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={stat.pendingFollowUps}
                        size="small"
                        icon={<WarningIcon />}
                        sx={{ backgroundColor: '#fef3c7', color: '#92400e' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={stat.completedFollowUps}
                        size="small"
                        icon={<CheckCircleIcon />}
                        sx={{ backgroundColor: '#d1fae5', color: '#065f46' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {stat.users}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={stat.organization.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          backgroundColor: stat.organization.is_active ? '#d1fae5' : '#fee2e2',
                          color: stat.organization.is_active ? '#065f46' : '#991b1b'
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Dashboard">
                        <IconButton
                          size="small"
                          onClick={() => handleViewOrganization(stat.organization.slug)}
                          sx={{ color: '#DC2626' }}
                        >
                          <DashboardIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {orgStats.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No organizations found
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default MasterDashboard;
